import { useMemo, useState, useEffect } from 'react';

import {
  IconStar,
  IconHash,
  IconStack2,
  IconSparkles,
} from '@tabler/icons-react';

import CustomImage from '@/components/CustomImage';
import CustomModal from '@/components/CustomModal';
import { useFormatText } from '@/hooks/useFormatText';
import { getQuarterViewGameDetails } from '@/service/gameService';
import { useSettingsStore } from '@/store/useSettingsStore';
import { IPreviewGameDetails } from '@/types/lesson';

interface IGameViewModalProps {
  gameId: string | null;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const GameDetailModal = ({
  gameId,
  isOpen,
  setIsOpen,
}: IGameViewModalProps) => {
  const { formatText } = useFormatText();
  const language = useSettingsStore((state) => state.settings.language);
  const [details, setDetails] = useState<IPreviewGameDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !gameId) return;

    let isMounted = true;
    setDetails(null);

    const fetchGameDetails = async () => {
      setIsLoading(true);
      try {
        const response = await getQuarterViewGameDetails(gameId);
        if (!isMounted) return;
        setDetails(response?.data ?? null);
      } catch (error) {
        console.error('Error fetching game details:', error);
        if (isMounted) setDetails(null);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchGameDetails();

    return () => {
      isMounted = false;
    };
  }, [gameId, isOpen]);

  const title = useMemo(() => details?.title || '', [details?.title]);

  const separator = language === 'ar' ? '، ' : ', ';
  const contentPreview = details?.content?.join(separator) || '-';
  const overviewText = isLoading
    ? formatText('common.loading')
    : details?.description ||
      formatText('create-lesson.view-modal.no-overview');

  if (!isOpen || !gameId) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      heading={title || formatText('create-lesson.view-modal.title')}
      position="right"
      childClassName="items-start gap-8 p-5 sm:p-6"
      dataTestId="game-view-modal"
    >
      <div className="w-full space-y-8">
        <section className="space-y-4">
          <h3 className="text-base font-semibold text-neutral-primary">
            {formatText('create-lesson.view-modal.overview')}
          </h3>
          <p className="text-sm leading-6 text-neutral-secondary min-h-12 capitalize">
            {overviewText}
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-neutral-hover">
                <IconSparkles size={18} />
              </div>
              <div className="space-y-1">
                <p className="text-sm text-neutral-secondary">
                  {formatText('create-lesson.filter.content')}
                </p>
                <p
                  className="text-base font-medium text-neutral-primary wrap-break-word"
                  dir={language === 'ar' ? 'rtl' : 'ltr'}
                >
                  {contentPreview}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-neutral-hover">
                <IconStar size={18} />
              </div>
              <div className="space-y-1">
                <p className="text-sm text-neutral-secondary">
                  {formatText('create-lesson.columns.stage')}
                </p>
                <p className="text-base font-medium text-neutral-primary">
                  {details?.stage || '-'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-neutral-hover">
                <IconHash size={18} />
              </div>
              <div className="space-y-1">
                <p className="text-sm text-neutral-secondary">
                  {formatText('create-lesson.view-modal.round')}
                </p>
                <p className="text-base font-medium text-neutral-primary">
                  {details?.rounds ?? '-'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-neutral-hover">
                <IconStack2 size={18} />
              </div>
              <div className="space-y-1">
                <p className="text-sm text-neutral-secondary">
                  {formatText('create-lesson.view-modal.quarter')}
                </p>
                <p className="text-base font-medium text-neutral-primary">
                  {details?.quarters || '-'}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h3 className="text-base font-semibold text-neutral-primary">
            {formatText('create-lesson.view-modal.preview')}
          </h3>
          {details?.preview_image ? (
            <CustomImage
              src={details.preview_image}
              alt={title}
              className="w-full rounded-2xl border border-neutral-light-200 object-cover aspect-video"
            />
          ) : (
            <div className="flex aspect-video w-full items-center justify-center rounded-2xl border border-dashed border-neutral-light-200 bg-white overflow-hidden">
              <p className="text-sm text-neutral-secondary">
                {formatText('create-lesson.view-modal.no-overview')}
              </p>
            </div>
          )}
        </section>
      </div>
    </CustomModal>
  );
};

export default GameDetailModal;
